import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { COMPUTED_KEYS, computeCell, fmtCell } from "@/lib/compute";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UnclockedReasonDialog, unclockedViolations } from "@/components/UnclockedReasonDialog";
import { HkRoomsCheckDialog, HkRoomsSummary } from "@/components/HkRoomsCheck";
import { MultiDeptVerify } from "@/components/MultiDeptVerify";
import { todayStr } from "@/lib/format";
import { ArrowLeft, Loader2, Save, Send, Plus, Trash2, BarChart3, Users2, AlertTriangle } from "lucide-react";

const DEFAULT_MIN_PER_ROOM = 30.0; // fallback when a housekeeper has no trailing-30-day room history
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function DailyReportForm() {
  const { propertyId: pParam, date: dParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isView = Boolean(pParam && dParam);
  const readOnly = user?.role === "owner";
  const canSubmit = can(user, ["admin", "regional", "gm", "finance"]);

  const [columns, setColumns] = useState(null);
  const [props, setProps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [propertyId, setPropertyId] = useState(pParam || searchParams.get("property") || "");
  const [date, setDate] = useState(dParam || todayStr());
  const [values, setValues] = useState({});
  const [payrollMap, setPayrollMap] = useState({}); // { "eid::dept": {hours_worked, stayover_cleaned, checkout_cleaned} }
  const [activePositions, setActivePositions] = useState({}); // { eid: Set(dept) } positions worked/clocked today
  const [splitSug, setSplitSug] = useState({}); // { eid: {hk_dept, other_dept, hk_hours, other_hours, ...} }
  const [clockedHours, setClockedHours] = useState({}); // { eid: total approved clocked hours (day) }
  const [clockedByPos, setClockedByPos] = useState({}); // { eid: { dept: clocked hrs } }
  const [flaggedPunches, setFlaggedPunches] = useState([]); // too-long punches excluded from payroll
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifiedMulti, setVerifiedMulti] = useState(new Set()); // multi-department employees the GM ticked
  const [unclocked, setUnclocked] = useState(null); // { violations, status } from server soft-block
  const [hkMeta, setHkMeta] = useState({ prevSold: null, range: [15, 45] });
  const [punchedEmps, setPunchedEmps] = useState(new Set()); // employees whose hours are locked to the Time Clock
  const [clockChanges, setClockChanges] = useState([]); // saved hours replaced by the clock on load
  const [hkCheck, setHkCheck] = useState(null); // { issues, totalRooms, prevSold, overOccupied, status }
  const [chargebacks, setChargebacks] = useState([]);
  const [cbAmount, setCbAmount] = useState("");
  const [cbNote, setCbNote] = useState("");
  const [cbBusy, setCbBusy] = useState(false);

  const loadChargebacks = () => {
    if (!propertyId || !date) return;
    api.get("/chargebacks", { params: { property_id: propertyId, date } })
      .then((r) => setChargebacks(r.data)).catch(() => setChargebacks([]));
  };
  useEffect(loadChargebacks, [propertyId, date]);

  const addChargeback = async () => {
    const amt = Number(cbAmount);
    if (!amt || amt <= 0) return toast.error("Enter a chargeback amount");
    setCbBusy(true);
    try {
      await api.post("/chargebacks", { property_id: propertyId, report_date: date, amount: amt, note: cbNote.trim() });
      setCbAmount(""); setCbNote("");
      loadChargebacks();
      toast.success("Chargeback added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCbBusy(false);
    }
  };
  const delChargeback = async (cid) => {
    await api.delete(`/chargebacks/${cid}`);
    loadChargebacks();
  };

  const HK_DEPTS = ["housekeeping", "houseman", "laundry", "executive"];
  const empById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);
  const empPositions = (e) => {
    const primary = e.department || "";
    const out = [{ department: primary, rate: e.rate_of_pay }];
    (e.positions || []).forEach((p) => {
      if (p.department && p.department !== primary) out.push({ department: p.department, rate: p.rate_of_pay ?? e.rate_of_pay });
    });
    return out;
  };
  // one visible row per (employee, position): active staff show their primary + any position worked
  // today; terminated staff appear ONLY for positions where they actually have activity that day.
  const payrollRows = useMemo(() => {
    const out = [];
    employees.forEach((e) => {
      const active = activePositions[e.id];
      const isEmployed = (e.status || "active") !== "terminated";
      empPositions(e).forEach((pos, idx) => {
        const isPrimary = idx === 0;
        if ((isPrimary && isEmployed) || (active && active.has(pos.department))) {
          out.push({ e, department: pos.department, key: `${e.id}::${pos.department}`, isPrimary });
        }
      });
    });
    out.sort((a, b) =>
      (a.department || "").localeCompare(b.department || "") ||
      (a.e.last_name || "").localeCompare(b.e.last_name || ""));
    return out;
  }, [employees, activePositions]);

  const prop = useMemo(() => props.find((p) => p.id === propertyId), [props, propertyId]);
  const totalRooms = Number(prop?.rooms) || 0;
  const visibleColumns = useMemo(() => {
    const hidden = new Set(prop?.hidden_columns || []);
    return (columns || []).filter((c) => !hidden.has(c.key));
  }, [columns, prop]);

  useEffect(() => {
    Promise.all([api.get("/grid-config"), api.get("/properties")]).then(([c, p]) => {
      setColumns(c.data.columns);
      setProps(p.data);
      if (!pParam && !searchParams.get("property") && p.data[0]) setPropertyId(p.data[0].id);
      setLoading(false);
    });
  }, [pParam, searchParams]);

  useEffect(() => {
    if (!propertyId) return;
    api.get("/employees", { params: { property_id: propertyId, status: "all" } })
      .then((r) => setEmployees([...r.data].sort((a, b) =>
        (a.department || "").localeCompare(b.department || "") ||
        (a.last_name || "").localeCompare(b.last_name || "")
      )))
      .catch(() => setEmployees([]));
  }, [propertyId]);

  useEffect(() => {
    if (dParam) setDate(dParam);
  }, [dParam]);

  useEffect(() => {
    if (propertyId && date) {
      api.get(`/daily-reports?property_id=${propertyId}&date=${date}`).then((r) => setValues(r.data[0]?.values || {})).catch(() => {});
      api.get("/daily-payroll", { params: { property_id: propertyId, date } }).then((r) => {
        const clockedByPos = r.data.clocked_by_position || {};
        const cleaned = r.data.cleaned_rooms || {};
        const m = {};
        const active = {};
        const addActive = (eid, dep) => { (active[eid] = active[eid] || new Set()).add(dep); };
        const emap = Object.fromEntries((employees || []).map((e) => [e.id, e]));
        // saved entries (keyed by position; legacy entries fall back to the employee's primary dept)
        (r.data.entries || []).forEach((e) => {
          const dep = e.position || emap[e.employee_id]?.department || "";
          const key = `${e.employee_id}::${dep}`;
          m[key] = {
            hours_worked: e.hours_worked ?? "",
            stayover_cleaned: e.stayover_cleaned ?? "",
            checkout_cleaned: e.checkout_cleaned ?? "",
            rooms_cleaned: e.rooms_cleaned ?? "",
          };
          addActive(e.employee_id, dep);
        });
        const splits = {}; // hours always come from each position's punches — no room-based splits
        // Anyone who punched today: hours per position = exactly what was clocked in that position.
        // Any older saved number is replaced; positions they didn't clock into get no hours (rooms kept).
        const punched = new Set([...Object.keys(r.data.clocked_hours || {}), ...(r.data.flagged || []).map((f) => f.employee_id)]);
        const changes = [];
        punched.forEach((eid) => {
          const byDep = clockedByPos[eid] || {};
          const emp = emap[eid];
          const deps = new Set([...(emp ? empPositions(emp).map((p) => p.department) : []), ...Object.keys(byDep)]);
          deps.forEach((dep) => {
            const key = `${eid}::${dep}`;
            const ex = m[key];
            const hrs = Number(byDep[dep] || 0) > 0 ? r2(Number(byDep[dep])) : "";
            const prev = ex && ex.hours_worked !== "" && ex.hours_worked != null ? Number(ex.hours_worked) : 0;
            if (hrs !== "") addActive(eid, dep);
            if (!ex && hrs === "") return;
            if (r.data.entries?.length && Math.abs(prev - (Number(hrs) || 0)) > 0.009 && emp) {
              changes.push({ name: `${emp.first_name} ${emp.last_name}`, dep, from: prev, to: Number(hrs) || 0 });
            }
            m[key] = { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...(ex || {}), hours_worked: hrs };
          });
        });
        setClockChanges(changes);
        setPunchedEmps(punched);
        // pre-fill Stayover/Checkout from kiosk cleanings → the housekeeping position row
        Object.entries(cleaned).forEach(([eid, c]) => {
          const emp = emap[eid];
          if (!emp) return;
          const positions = empPositions(emp);
          const dep = splits[eid]?.hk_dept
            || (positions.find((p) => (p.department || "").toLowerCase().includes("housekeep")) || positions[0]).department;
          const key = `${eid}::${dep}`;
          addActive(eid, dep);
          const ex = m[key] || { hours_worked: "" };
          const so = ex.stayover_cleaned;
          const co = ex.checkout_cleaned;
          const soBlank = so === "" || so == null || Number(so) === 0;
          const coBlank = co === "" || co == null || Number(co) === 0;
          m[key] = {
            ...ex,
            stayover_cleaned: soBlank ? c.stayover : so,
            checkout_cleaned: coBlank ? c.checkout : co,
          };
        });
        // split-shift suggestion: HK hours from rooms x avg min/room, remainder -> other dept.
        // Applied over blank/zero fields only (never overwrites a hand-keyed value).
        Object.entries(splits).forEach(([eid, s]) => {
          const hkKey = `${eid}::${s.hk_dept}`;
          const otKey = `${eid}::${s.other_dept}`;
          addActive(eid, s.hk_dept); addActive(eid, s.other_dept);
          const hkEx = m[hkKey] || {};
          const otEx = m[otKey] || {};
          const hkBlank = hkEx.hours_worked === "" || hkEx.hours_worked == null || Number(hkEx.hours_worked) === 0;
          const otBlank = otEx.hours_worked === "" || otEx.hours_worked == null || Number(otEx.hours_worked) === 0;
          if (hkBlank) m[hkKey] = { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...hkEx, hours_worked: s.hk_hours };
          if (otBlank) m[otKey] = { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...otEx, hours_worked: s.other_hours };
        });
        setSplitSug(splits);
        setPayrollMap(m);
        setActivePositions(active);
        setClockedHours(r.data.clocked_hours || {});
        setClockedByPos(clockedByPos);
        setFlaggedPunches(r.data.flagged || []);
        setVerifiedMulti(new Set(r.data.multi_dept_verified || []));
        setHkMeta({ prevSold: r.data.prev_rooms_sold ?? null, range: r.data.hk_min_per_room_range || [15, 45] });
      }).catch(() => { setPayrollMap({}); setActivePositions({}); setSplitSug({}); setClockedHours({}); setClockedByPos({}); setFlaggedPunches([]); });
    }
  }, [propertyId, date, employees]);

  useEffect(() => {
    if (!totalRooms) return;
    setValues((v) => (v.rooms_available === undefined || v.rooms_available === "" ? { ...v, rooms_available: totalRooms } : v));
  }, [totalRooms]);

  const setV = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const propName = useMemo(() => props.find((p) => p.id === propertyId)?.name, [props, propertyId]);
  const empName = (e) => `${e.first_name} ${e.last_name}`;

  const setPayCell = (key, k, val) =>
    setPayrollMap((m) => ({ ...m, [key]: { hours_worked: "", stayover_cleaned: "", checkout_cleaned: "", ...(m[key] || {}), [k]: val } }));

  const applySplit = (eid) => {
    const s = splitSug[eid];
    if (!s) return;
    setPayrollMap((m) => ({
      ...m,
      [`${eid}::${s.hk_dept}`]: { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...(m[`${eid}::${s.hk_dept}`] || {}), hours_worked: s.hk_hours },
      [`${eid}::${s.other_dept}`]: { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...(m[`${eid}::${s.other_dept}`] || {}), hours_worked: s.other_hours },
    }));
    toast.success(`Split applied: ${s.hk_hours} hr ${s.hk_dept} / ${s.other_hours} hr ${s.other_dept}`);
  };

  const roomsTotal = (row = {}) => {
    const hasSplit = (row.stayover_cleaned ?? "") !== "" || (row.checkout_cleaned ?? "") !== "";
    const split = (Number(row.stayover_cleaned) || 0) + (Number(row.checkout_cleaned) || 0);
    if (split > 0) return split;
    if (Number(row.rooms_cleaned) > 0) return Number(row.rooms_cleaned); // total-only (e.g. restored rooms)
    return hasSplit ? 0 : row.rooms_cleaned ?? "";
  };

  // a person's day is a fixed pie: their clocked hours are ALLOCATED across positions, never duplicated
  const allocByEmp = useMemo(() => {
    const out = {};
    payrollRows.forEach((pr) => {
      const r = payrollMap[pr.key];
      const h = r && r.hours_worked !== "" && r.hours_worked != null ? Number(r.hours_worked) || 0 : 0;
      const o = out[pr.e.id] || (out[pr.e.id] = { allocated: 0, clocked: Number(clockedHours[pr.e.id] || 0) });
      o.allocated = r2(o.allocated + h);
    });
    return out;
  }, [payrollRows, payrollMap, clockedHours]);

  // for an employee with exactly 2 positions where one is Housekeeping, return {hk, other}
  const hkOther = (eid) => {
    const emp = empById[eid];
    if (!emp) return null;
    const deps = empPositions(emp).map((p) => p.department).filter(Boolean);
    if (deps.length !== 2) return null;
    const hk = deps.find((d) => (d || "").toLowerCase().includes("housekeep"));
    const other = deps.find((d) => d !== hk);
    return hk && other ? { hk, other } : null;
  };

  // typing rooms into the Housekeeping row auto-recomputes the split so the day total never changes
  const setRoomCell = (pr, field, val) => {
    setPayrollMap((m) => {
      const next = { ...m, [pr.key]: { hours_worked: "", stayover_cleaned: "", checkout_cleaned: "", ...(m[pr.key] || {}), [field]: val } };
      return next;
    });
  };

  // re-allocate a person's clocked hours across their positions (split by rooms, else to the clocked position)
  const autoBalance = (eid) => {
    const total = Number(clockedHours[eid] || 0);
    if (total <= 0) return;
    const emp = empById[eid];
    const deps = empPositions(emp).map((p) => p.department).filter(Boolean);
    const ho = hkOther(eid);
    setPayrollMap((m) => {
      const next = { ...m };
      const hkRow = ho ? (m[`${eid}::${ho.hk}`] || {}) : {};
      const rooms = (Number(hkRow.stayover_cleaned) || 0) + (Number(hkRow.checkout_cleaned) || 0) || (Number(hkRow.rooms_cleaned) || 0);
      if (ho && rooms > 0) {
        const avg = splitSug[eid]?.avg_min_per_room || DEFAULT_MIN_PER_ROOM;
        const hkH = r2(Math.min(total, (rooms * avg) / 60));
        next[`${eid}::${ho.hk}`] = { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...(next[`${eid}::${ho.hk}`] || {}), hours_worked: hkH };
        next[`${eid}::${ho.other}`] = { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...(next[`${eid}::${ho.other}`] || {}), hours_worked: r2(total - hkH) };
      } else {
        const byPos = clockedByPos[eid] || {};
        const clockedDeps = deps.filter((d) => Number(byPos[d] || 0) > 0);
        deps.forEach((d) => {
          const k = `${eid}::${d}`;
          const ch = Number(byPos[d] || 0);
          next[k] = { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...(next[k] || {}), hours_worked: ch > 0 ? r2(ch) : "" };
        });
        // no per-position clock tags at all -> put the whole day on the primary position
        if (clockedDeps.length === 0 && deps[0]) {
          next[`${eid}::${deps[0]}`] = { stayover_cleaned: "", checkout_cleaned: "", rooms_cleaned: "", ...(next[`${eid}::${deps[0]}`] || {}), hours_worked: r2(total) };
        }
      }
      return next;
    });
    toast.success("Hours balanced to the clocked total");
  };

  const multiDept = (() => {
    const byEmp = {};
    payrollRows.forEach((pr) => {
      const r = payrollMap[pr.key];
      const h = r && r.hours_worked !== "" && r.hours_worked != null ? Number(r.hours_worked) || 0 : 0;
      if (h > 0) (byEmp[pr.e.id] = byEmp[pr.e.id] || []).push({ pr, hours: h });
    });
    return Object.entries(byEmp).filter(([, rows]) => rows.length > 1).map(([eid, rows]) => ({
      eid, rows, name: empById[eid] ? empName(empById[eid]) : "Employee",
      total: rows.reduce((a, x) => a + x.hours, 0), clocked: Number(clockedHours[eid] || 0),
    }));
  })();
  const toggleVerified = (eid) => setVerifiedMulti((s0) => { const n = new Set(s0); if (n.has(eid)) n.delete(eid); else n.add(eid); return n; });

  const hkTotalRooms = payrollRows.reduce((a, pr) => a + (Number(roomsTotal(payrollMap[pr.key] || {})) || 0), 0);
  const buildHkCheck = () => {
    const [lo, hi] = hkMeta.range;
    const issues = [];
    payrollRows.forEach((pr) => {
      if (!(pr.department || "").toLowerCase().includes("housekeep")) return;
      const r = payrollMap[pr.key] || {};
      const h = Number(r.hours_worked) || 0;
      if (h <= 0) return;
      const rooms = Number(roomsTotal(r)) || 0;
      const base = { key: pr.key, eid: pr.e.id, name: empName(pr.e), hours: h, rooms, mpr: rooms ? (h * 60) / rooms : null };
      if (rooms === 0) issues.push({ ...base, kind: "zero" });
      else if (base.mpr < lo) issues.push({ ...base, kind: "fast" });
      else if (base.mpr > hi) issues.push({ ...base, kind: "slow" });
    });
    const overOccupied = hkMeta.prevSold != null && hkTotalRooms > hkMeta.prevSold;
    return issues.length || overOccupied ? { issues, totalRooms: hkTotalRooms, prevSold: hkMeta.prevSold, overOccupied } : null;
  };

  const save = async (status, confirmedDoublePos = false, overrideReasons = null, confirmedHk = false) => {
    if (!propertyId) return toast.error("Select a property");
    // validate: if rooms entered, hours must be entered too
    for (const row of payrollRows) {
      const r = payrollMap[row.key];
      if (r && Number(roomsTotal(r)) > 0 && (r.hours_worked === "" || r.hours_worked == null)) {
        return toast.error(`Enter hours worked for ${empName(row.e)} (${row.department})`);
      }
    }
    // on Submit: a person's allocated hours across positions must equal their clocked total
    if (status === "submitted") {
      for (const eid of Object.keys(allocByEmp)) {
        const b = allocByEmp[eid];
        if (b.clocked > 0 && Math.abs(b.allocated - b.clocked) > 0.01) {
          const e = empById[eid];
          return toast.error(`${e ? empName(e) : "An employee"} is allocated ${b.allocated.toFixed(2)} hrs but clocked ${b.clocked.toFixed(2)} hrs. Use "Auto-balance" so the day totals match before submitting.`);
        }
      }
    }
    // multi-department employees must be ticked "Verified" in the box at the top of Labor
    const unverified = multiDept.filter((x) => !verifiedMulti.has(x.eid));
    if (unverified.length) {
      document.getElementById("multi-dept-verify")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return toast.error(`Verify ${unverified.map((x) => x.name).join(", ")} in "Worked in 2+ departments" before saving`);
    }
    // housekeeper rooms check: hours with 0 rooms, min/room outside standard, or more rooms than occupied
    if (!confirmedHk) {
      const chk = buildHkCheck();
      if (chk) { setHkCheck({ ...chk, status }); return; }
    }
    setSaving(true);
    try {
      const clean = {};
      Object.keys(values).forEach((k) => { if (!COMPUTED_KEYS.has(k)) clean[k] = values[k]; });
      const entries = payrollRows
        .filter((row) => { const r = payrollMap[row.key]; return r && r.hours_worked !== "" && r.hours_worked != null; })
        .map((row) => ({
          employee_id: row.e.id,
          position: row.department,
          hours_worked: Number(payrollMap[row.key].hours_worked) || 0,
          stayover_cleaned: Number(payrollMap[row.key].stayover_cleaned) || 0,
          checkout_cleaned: Number(payrollMap[row.key].checkout_cleaned) || 0,
          rooms_cleaned: Number(roomsTotal(payrollMap[row.key])) || 0,
        }));
      await api.post("/daily-payroll/save", {
        property_id: propertyId, date, entries, override_reasons: overrideReasons || {},
        multi_dept_verified: multiDept.filter((x) => verifiedMulti.has(x.eid)).map((x) => x.eid),
      });
      await api.post("/daily-reports", { property_id: propertyId, report_date: date, values: clean, status });
      setUnclocked(null);
      toast.success(status === "submitted" ? "Report submitted" : "Draft saved");
      if (!isView) navigate(`/daily-reports/${propertyId}/${date}`);
    } catch (e) {
      const viol = unclockedViolations(e);
      if (viol) { setUnclocked({ violations: viol, status }); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !columns) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const cellValue = (col) => (COMPUTED_KEYS.has(col.key) ? computeCell(col.key, values, totalRooms) : values[col.key] ?? "");

  return (
    <div className="space-y-8 pb-16" data-testid="report-form-page">
      <div>
        <button onClick={() => navigate("/daily-reports")} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to reports
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="label-caps text-primary">Daily Manager Report</div>
            <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">{isView ? propName : "New Daily Report"}</h1>
          </div>
          {!readOnly && canSubmit && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Draft
              </Button>
              <Button onClick={() => save("submitted")} disabled={saving} data-testid="submit-report-btn">
                <Send className="mr-2 h-4 w-4" /> Submit
              </Button>
            </div>
          )}
        </div>
      </div>

      <Card className="rounded-none border p-6">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Property</Label>
            <Select value={propertyId} onValueChange={setPropertyId} disabled={isView || readOnly}>
              <SelectTrigger data-testid="report-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
              <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Report Date</Label>
            <Input
              type="date"
              value={date}
              disabled={readOnly}
              onChange={(e) => {
                const nd = e.target.value;
                if (!nd) return;
                setDate(nd);
                if (isView) navigate(`/daily-reports/${propertyId}/${nd}`);
              }}
              data-testid="report-date"
            />
          </div>
        </div>
      </Card>

      <Tabs defaultValue="stats">
        <TabsList className="rounded-none">
          <TabsTrigger value="stats" data-testid="tab-hotel-stats"><BarChart3 className="mr-2 h-4 w-4" /> Hotel Statistics</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Users2 className="mr-2 h-4 w-4" /> Payroll Entry</TabsTrigger>
        </TabsList>

        <TabsContent value="stats" className="mt-6">
          <Card className="rounded-none border p-6">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {visibleColumns.map((c) => {
                const computed = COMPUTED_KEYS.has(c.key);
                const isNotes = c.type === "notes";
                return (
                  <div key={c.key} className={`space-y-1.5 ${isNotes ? "sm:col-span-2 lg:col-span-3" : ""}`}>
                    <Label className="flex items-center gap-1">
                      {c.label}
                      {computed && <span className="text-primary" title="Calculated automatically">ƒ</span>}
                    </Label>
                    {computed ? (
                      <div className="flex h-10 items-center rounded-md border border-border bg-accent/30 px-3 font-mono text-sm text-accent-foreground" data-testid={`field-${c.key}`}>
                        {fmtCell(c.type, cellValue(c)) || "—"}
                      </div>
                    ) : c.type === "checkbox" ? (
                      <div className="flex h-10 items-center">
                        <Checkbox checked={!!values[c.key]} disabled={readOnly} onCheckedChange={(v) => setV(c.key, !!v)} data-testid={`field-${c.key}`} />
                      </div>
                    ) : isNotes ? (
                      <Textarea rows={2} disabled={readOnly} value={values[c.key] ?? ""} onChange={(e) => setV(c.key, e.target.value)} data-testid={`field-${c.key}`} />
                    ) : (
                      <Input type="text" inputMode={c.type === "text" ? "text" : "decimal"} disabled={readOnly} value={values[c.key] ?? ""} onChange={(e) => setV(c.key, e.target.value)} data-testid={`field-${c.key}`} />
                    )}
                    {c.key === "over_short" && (
                      <Textarea rows={2} disabled={readOnly} value={values.over_short_comment ?? ""} onChange={(e) => setV("over_short_comment", e.target.value)} placeholder="Comment on the over/short — e.g. $20 short, till miscount (optional)" className="mt-1.5 text-sm" data-testid="field-over_short_comment" />
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="mt-6 rounded-none border p-6" data-testid="chargebacks-section">
            <div className="mb-1 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold">Chargebacks</h3>
              {chargebacks.length > 0 && (
                <span className="text-sm font-semibold" data-testid="chargebacks-day-total">
                  Total: ${chargebacks.reduce((s, c) => s + (Number(c.amount) || 0), 0).toFixed(2)}
                </span>
              )}
            </div>
            <p className="mb-4 text-xs text-muted-foreground">Enter any chargebacks received on this date (amount + guest/reason). They roll up into the Monthly Report.</p>

            {chargebacks.length === 0 ? (
              <p className="text-sm text-muted-foreground">No chargebacks recorded for this date.</p>
            ) : (
              <div className="space-y-2">
                {chargebacks.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 rounded-md border border-border bg-accent/20 px-3 py-2 text-sm" data-testid={`chargeback-row-${c.id}`}>
                    <span className="w-24 font-mono font-semibold tabular-nums">${Number(c.amount).toFixed(2)}</span>
                    <span className="flex-1 text-muted-foreground">{c.note || <span className="italic">No note</span>}</span>
                    <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground">{(c.status || "open").replace("_", " ")}</span>
                    {!readOnly && (
                      <button onClick={() => delChargeback(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`chargeback-del-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {!readOnly && (
              <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-border pt-4">
                <div className="space-y-1">
                  <Label className="text-xs">Amount ($)</Label>
                  <Input type="text" inputMode="decimal" placeholder="0.00" value={cbAmount}
                    onChange={(e) => setCbAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                    className="w-32" data-testid="chargeback-amount" />
                </div>
                <div className="min-w-[240px] flex-1 space-y-1">
                  <Label className="text-xs">Note (guest / reason)</Label>
                  <Input type="text" placeholder="e.g. J. Smith — disputed room charge" value={cbNote}
                    onChange={(e) => setCbNote(e.target.value)} data-testid="chargeback-note" />
                </div>
                <Button type="button" onClick={addChargeback} disabled={cbBusy} data-testid="chargeback-add-btn">
                  <Plus className="mr-2 h-4 w-4" /> Add
                </Button>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="payroll" className="mt-6">
          {flaggedPunches.length > 0 && (
            <div className="mb-4 rounded-none border border-red-400/50 bg-red-50 p-4 dark:bg-red-950/20" data-testid="flagged-punches-banner">
              <div className="mb-2 flex items-center gap-2 text-red-700 dark:text-red-400">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-bold">{flaggedPunches.length} punch{flaggedPunches.length > 1 ? "es" : ""} {flaggedPunches.length > 1 ? "need" : "needs"} review — excluded from payroll</span>
              </div>
              <p className="mb-2 text-xs text-muted-foreground">
                These punches are longer than 16 hours, almost always a missed clock-out. Fix the clock-out time on the <span className="font-medium text-foreground">Time Clock</span> page, then reopen this day.
              </p>
              <div className="space-y-1">
                {flaggedPunches.map((f) => (
                  <div key={f.id} className="flex items-center justify-between rounded-sm border border-red-400/40 bg-white px-3 py-1.5 text-sm dark:bg-transparent" data-testid={`flagged-punch-${f.id}`}>
                    <span className="font-medium">{f.employee_name}{f.overnight ? " · clocked out next day" : ""}</span>
                    <span className="font-mono font-bold text-red-600 dark:text-red-400">{Number(f.hours).toFixed(2)} h</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Card className="rounded-none border p-6" data-testid="payroll-section">
            {employees.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No employees for this property yet. Add them in the <span className="font-medium text-foreground">Employees</span> page first.
              </p>
            ) : (
              <div className="space-y-2">
                <MultiDeptVerify items={multiDept} payrollMap={payrollMap} verified={verifiedMulti} onToggle={toggleVerified} readOnly={readOnly} setRoomCell={setRoomCell} roomsTotal={roomsTotal} />
                <HkRoomsSummary totalRooms={hkTotalRooms} prevSold={hkMeta.prevSold} />
                <p className="text-xs text-muted-foreground" data-testid="payroll-clock-note">
                  Hours for anyone who clocked in come straight from their Time Clock punches for each position and can't be typed here. To change them, fix the punch on the <span className="font-medium text-foreground">Time Clock</span>.
                </p>
                {clockChanges.length > 0 && (
                  <div className="rounded-sm border border-amber-400/60 bg-amber-50 px-3 py-2 text-xs dark:bg-amber-950/20" data-testid="payroll-clock-changes">
                    <b>Updated from the Time Clock</b> (saved hours didn't match the punches — save to keep):
                    <ul className="mt-1 space-y-0.5">
                      {clockChanges.map((c) => <li key={`${c.name}-${c.dep}`}>{c.name} · {c.dep}: <span className="line-through">{c.from.toFixed(2)}</span> → <b>{c.to.toFixed(2)}</b></li>)}
                    </ul>
                  </div>
                )}
                <div className="grid grid-cols-12 gap-3 label-caps text-muted-foreground">
                  <div className="col-span-4">Employee</div>
                  <div className="col-span-2">Hours Worked</div>
                  <div className="col-span-2">Stayover</div>
                  <div className="col-span-2">Checkout</div>
                  <div className="col-span-2">Rooms Cleaned</div>
                </div>
                {payrollRows.map((pr, i) => {
                  const { e, department, key, isPrimary } = pr;
                  const row = payrollMap[key] || {};
                  const showDept = department !== (payrollRows[i - 1]?.department);
                  return (
                    <div key={key}>
                      {showDept && (
                        <div className="mt-4 border-b border-border pb-1 label-caps text-primary" data-testid={`payroll-dept-${department}`}>
                          {department || "Unassigned"}
                        </div>
                      )}
                      <div className="grid grid-cols-12 items-center gap-3 py-1.5" data-testid={`payroll-emp-row-${key}`}>
                        <div className="col-span-4 text-sm font-medium">
                          {empName(e)}
                          {!isPrimary && <span className="ml-2 rounded-sm bg-accent px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-accent-foreground">2nd position</span>}
                          {splitSug[e.id] && splitSug[e.id].hk_dept === department && (
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <span className="text-[0.65rem] text-muted-foreground" data-testid={`payroll-split-note-${e.id}`}>
                                {splitSug[e.id].rooms} rooms × {splitSug[e.id].avg_min_per_room} min{splitSug[e.id].used_default ? " (default)" : ""} = {splitSug[e.id].hk_hours} hr {splitSug[e.id].hk_dept}; {splitSug[e.id].other_hours} hr → {splitSug[e.id].other_dept}
                              </span>
                              {!readOnly && (
                                <button onClick={() => applySplit(e.id)} className="rounded-sm bg-primary px-2 py-0.5 text-[0.65rem] font-semibold text-primary-foreground hover:opacity-90" data-testid={`payroll-split-btn-${e.id}`}>
                                  Split by rooms
                                </button>
                              )}
                            </div>
                          )}
                          {isPrimary && (() => {
                            const b = allocByEmp[e.id];
                            const over = b && b.clocked > 0 && Math.abs(b.allocated - b.clocked) > 0.01;
                            if (!over) return null;
                            return (
                              <div className="mt-1 flex flex-wrap items-center gap-2" data-testid={`payroll-imbalance-${e.id}`}>
                                <span className="inline-flex items-center gap-1 rounded-sm bg-red-100 px-1.5 py-0.5 text-[0.65rem] font-semibold text-red-700 dark:bg-red-950/40 dark:text-red-400">
                                  <AlertTriangle className="h-3 w-3" /> Allocated {b.allocated.toFixed(2)} of {b.clocked.toFixed(2)} clocked hrs
                                </span>
                                {!readOnly && (
                                  <button onClick={() => autoBalance(e.id)} className="rounded-sm bg-primary px-2 py-0.5 text-[0.65rem] font-semibold text-primary-foreground hover:opacity-90" data-testid={`payroll-autobalance-${e.id}`}>
                                    Auto-balance
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="col-span-2">
                          <Input type="text" inputMode="decimal" placeholder="0" disabled={readOnly || punchedEmps.has(e.id)} value={row.hours_worked ?? ""} onChange={(ev) => setPayCell(key, "hours_worked", ev.target.value)} data-testid={`payroll-hours-${key}`} />
                          {punchedEmps.has(e.id) && <div className="mt-0.5 text-[0.6rem] text-muted-foreground" data-testid={`payroll-hours-locked-${key}`}>from Time Clock</div>}
                        </div>
                        <Input className="col-span-2" type="text" inputMode="decimal" placeholder="0" disabled={readOnly} value={row.stayover_cleaned ?? ""} onChange={(ev) => setRoomCell(pr, "stayover_cleaned", ev.target.value)} data-testid={`payroll-stayover-${key}`} />
                        <Input className="col-span-2" type="text" inputMode="decimal" placeholder="0" disabled={readOnly} value={row.checkout_cleaned ?? ""} onChange={(ev) => setRoomCell(pr, "checkout_cleaned", ev.target.value)} data-testid={`payroll-checkout-${key}`} />
                        <div className="col-span-2 flex h-10 items-center rounded-md border border-border bg-accent/30 px-3 font-mono text-sm text-accent-foreground" data-testid={`payroll-rooms-${key}`}>
                          {roomsTotal(row) === "" ? "—" : roomsTotal(row)}
                          {Number(row.rooms_cleaned) > 0 && !((Number(row.stayover_cleaned) || 0) + (Number(row.checkout_cleaned) || 0)) && (
                            <span className="ml-1 text-[0.55rem] uppercase text-muted-foreground" title="Total rooms only — stayover/checkout split not entered" data-testid={`payroll-rooms-total-only-${key}`}>total</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <p className="pt-3 text-xs text-muted-foreground">Enter hours for anyone who worked. Employees with more than one position show a row per position — their clocked hours are <span className="font-medium text-foreground">split across positions</span>, never duplicated. Typing rooms into the Housekeeping row auto-adjusts the split so the day total stays equal to their clocked hours. Stayover + Checkout auto-total to Rooms Cleaned.</p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Double-position verification — GM confirms the split before saving */}
      <UnclockedReasonDialog
        violations={unclocked?.violations || null}
        busy={saving}
        onCancel={() => setUnclocked(null)}
        onConfirm={(reasons) => save(unclocked.status, true, reasons, true)}
      />
      <HkRoomsCheckDialog
        check={hkCheck}
        range={hkMeta.range}
        onCancel={() => setHkCheck(null)}
        onConfirm={() => { const st = hkCheck.status; setHkCheck(null); save(st, true, null, true); }}
      />
    </div>
  );
}
